import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import GdsHouseholds from '~/glm/models/gds/gds-household-collection';
import AnalyticsStore from '~/glm/models/analytics-store';
import GdsUiState from '~/glm/models/gds/gds-ui-state';
import GdsGuest from '~/glm/models/gds/gds-guest-model.js';
import GuestRow from '~/glm/components/guest-list/guest-row';
import OverviewGuestRow from '~/glm/components/guest-list/overview-guest-row';
import EventCollection from '~/glm/models/gds/event-collection';
import NoMatchMessage from '~/glm/components/guest-list/no-match-message';

@inject('gdsUiState', 'gdsEvents')
@observer
class IndividualGuestList extends Component {
  static propTypes = {
    guests: PropTypes.arrayOf(PropTypes.instanceOf(GdsGuest)).isRequired,
    gdsUiState: PropTypes.instanceOf(GdsUiState),
    gdsHouseholds: PropTypes.instanceOf(GdsHouseholds).isRequired,
    analyticsStore: PropTypes.instanceOf(AnalyticsStore).isRequired,
    editHousehold: PropTypes.func.isRequired,
    trackThankYou: PropTypes.func.isRequired,
    trackMeal: PropTypes.func.isRequired,
    gdsEvents: PropTypes.instanceOf(EventCollection),
    usesQuestions: PropTypes.bool.isRequired,
  };

  componentDidMount() {
    const { analyticsStore, gdsHouseholds } = this.props;
    analyticsStore.track('Viewed Guest List', {
      guest_count: gdsHouseholds.guestsCount || 0,
      household_count: gdsHouseholds.size || 0,
    });
  }

  get activeFilters() {
    const { gdsUiState } = this.props;
    const filters = [];
    if (gdsUiState.filter) filters.push('search');
    if (gdsUiState.rsvpFilter) filters.push('rsvp');
    if (gdsUiState.mealFilter) filters.push('meal');
    if (gdsUiState.addressFilter) filters.push('address');

    return filters;
  }

  setFilterResultsLength(filterResults) {
    const { gdsUiState } = this.props;

    if (gdsUiState.filterResultsLength !== filterResults) {
      gdsUiState.setFilterResultsLength(filterResults);
    }
  }

  getFilteredGuests = () => {
    const { gdsUiState, guests } = this.props;
    const event = this.getEventForFilter(gdsUiState);
    return guests.filter(guest => {
      const invitation = event ? guest.getInvitationByEvent(event.id) : null;
      const found = this.matchesFilter(guest, invitation);
      return found;
    });
  };

  getEventForFilter(gdsUiState) {
    let event = null;
    if (gdsUiState.selectedEvent) {
      event = gdsUiState.selectedEvent;
    } else if (gdsUiState.rsvpFilteredEvent) {
      event = gdsUiState.rsvpFilteredEvent;
    }
    return event;
  }

  rsvpFilterMatch(rsvpStatus, eventId) {
    const { gdsUiState } = this.props;
    return (
      gdsUiState.rsvpFilter &&
      gdsUiState.rsvpFilteredEvent &&
      gdsUiState.rsvpFilteredEvent.id === eventId &&
      rsvpStatus === gdsUiState.rsvpFilter
    );
  }

  mealFilterMatch(invitation) {
    const { gdsUiState, gdsEvents } = this.props;
    const event = gdsEvents.find(event => event.id === invitation.eventId);
    const mealQuestion = event.mealQuestion;

    return gdsUiState.mealFilter && invitation.getMealAnswer(mealQuestion) === gdsUiState.mealFilter;
  }

  addressFilterMatch(guest) {
    const { gdsHouseholds } = this.props;
    const household = gdsHouseholds.getHouseholdById(guest.householdId);
    return !household.address;
  }

  searchFilterMatch(guest) {
    const { gdsUiState } = this.props;
    const query = gdsUiState.filter.toLowerCase();
    const displayName = guest.displayName.toLowerCase();

    return query ? displayName.includes(query) : false;
  }

  matchesFilter = (guest, invitation) => {
    const filters = this.activeFilters;
    const { gdsUiState } = this.props;
    return filters.every(filter => {
      if (filter === 'search') {
        return this.searchFilterMatch(guest);
      } else if (filter === 'rsvp' && invitation) {
        return this.rsvpFilterMatch(invitation.displayRsvp, invitation.eventId);
      } else if (filter === 'rsvp') {
        return gdsUiState.rsvpFilter === 'Not Invited';
      } else if (filter === 'meal' && invitation) {
        return this.mealFilterMatch(invitation);
      } else if (filter === 'address') {
        return this.addressFilterMatch(guest);
      }
      return null;
    });
  };

  sortFunction = (a, b) => {
    const { gdsUiState } = this.props;
    const direction = gdsUiState.sort.direction;
    let first;

    if (a.lastName < b.lastName) first = a;
    else if (a.lastName > b.lastName) first = b;
    else if (a.firstName < b.firstName) first = a;
    else first = b;

    if (direction === 'ascending') {
      return first === a ? -1 : 1;
    } else if (direction === 'descending') {
      return first === a ? 1 : -1;
    }
  };

  render() {
    const {
      gdsUiState,
      trackMeal,
      editHousehold,
      analyticsStore,
      gdsHouseholds,
      trackThankYou,
      gdsEvents,
      usesQuestions,
    } = this.props;

    let filteredGuests = this.getFilteredGuests();

    this.setFilterResultsLength(filteredGuests.length);

    if (filteredGuests.length) {
      if (gdsUiState.sort.direction) {
        filteredGuests = filteredGuests.sort(this.sortFunction);
      }

      return filteredGuests.map(guest => {
        const household = gdsHouseholds.getHouseholdById(guest.householdId);

        if (gdsUiState.selectedEvent) {
          const event = gdsUiState.selectedEvent;
          const invitation = guest.getInvitationByEvent(event.id);
          return (
            <GuestRow
              rsvpEnabled={gdsEvents.rsvpEnabled}
              event={event}
              household={household}
              guest={guest}
              invitation={invitation}
              key={guest.id}
              editHousehold={editHousehold}
              analyticsStore={analyticsStore}
              trackMeal={trackMeal}
              trackThankYou={trackThankYou}
              usesQuestions={usesQuestions}
            />
          );
        } else {
          return (
            <OverviewGuestRow
              gdsEvents={gdsEvents}
              household={household}
              gdsUiState={gdsUiState}
              guest={guest}
              key={guest.id}
              editHousehold={editHousehold}
              analyticsStore={analyticsStore}
              trackMeal={trackMeal}
              trackThankYou={trackThankYou}
            />
          );
        }
      });
    } else {
      return <NoMatchMessage />;
    }
  }
}

export default IndividualGuestList;
